import type { Logger } from "@expert/logging";

import { trackFullStoryEvent } from "@expert/monitoring";

import { getRootDispatcher } from "@/analytics";
import { type HoneycombEnrolledProducts, sendToHoneycomb } from "@/analytics/sendToHoneycomb";
import { endDynatraceAction, startDynatraceAction } from "@/utils/RealUserMonitoringClient";

import type { Enrollment, EnrollmentResponse } from "../types";

import { useProductOfferStore } from "..";
import { EnrollmentStatus, SaleProgress } from "../types";
import { getSalesApiClient } from "./api";

export const createEnrollment = async (logger: Logger, enrollment: Enrollment) => {
    const childLogger = logger.child({ module: "enrollment", productOfferId: enrollment.productOfferId });
    startDynatraceAction("SaleEnrollment", enrollment.productOfferId);
    try {
        childLogger.info("Making enrollment request");
        const result = await getSalesApiClient().post("v2/enrollment", { json: enrollment }).json<EnrollmentResponse>();
        childLogger.info("Finished enrollment request");

        void getRootDispatcher().dispatchBusinessEvent("GetEnrollmentStatus", {
            ProductOfferId: enrollment.productOfferId,
            enrollmentStatus: result.enrollmentStatus,
            StatusOrigin: "ASP",
            productSku: result.productSku,
            confirmationCode: enrollment.confirmationCode,
        });

        const { getProducts, getProductInfo } = useProductOfferStore.getState();
        const enrolledProducts: HoneycombEnrolledProducts[] = getProducts(enrollment.sessionId)
            .filter((p) => p.saleProgress === SaleProgress.Success)
            .map((p) => {
                return {
                    productSku: p.productSku,
                    productOfferId: p.id,
                    enrollmentStatus: p.saleProgress,
                    statusOrigin: p.salesProvider,
                    displayName: getProductInfo(enrollment.sessionId, p?.productSku)?.displayName,
                };
            });
        const productInfo = getProductInfo(enrollment.sessionId, enrollment?.productSku);
        const newEnrollment: HoneycombEnrolledProducts = {
            enrollmentStatus: result.enrollmentStatus === "SUCCESS" ? SaleProgress.Success : SaleProgress.Failure,
            productSku: enrollment.productSku,
            productOfferId: enrollment.productOfferId,
            statusOrigin: "asp", //all products using this enrollment method go through asp currently
            displayName: productInfo?.displayName,
        };
        const honeycombEnrollment: HoneycombEnrolledProducts[] = enrolledProducts.concat([newEnrollment]);
        sendToHoneycomb(enrollment.sessionId, { enrollment: honeycombEnrollment });

        const isSuccess = result.enrollmentStatus === "SUCCESS";
        const eventName = isSuccess ? "EnrollmentSuccess" : "EnrollmentFailure";

        trackFullStoryEvent(eventName, {
            productSku: enrollment.productSku,
            productOfferId: enrollment.productOfferId,
            sessionId: enrollment.sessionId,
            partner: enrollment.partner,
            enrollmentStatus: result.enrollmentStatus,
            statusOrigin: "asp",
        });

        endDynatraceAction(
            "SaleEnrollment",
            isSuccess ? undefined : { taskId: enrollment.productOfferId, error: eventName },
        );

        return result;
    } catch (error: unknown) {
        void getRootDispatcher().dispatchBusinessEvent("EnrollmentError", {
            Status: "LAMBDA_ERROR",
            ProductOfferId: enrollment.productOfferId,
            Error: error,
            productSku: enrollment.productSku,
        });

        childLogger.warn({ error, enrollment }, "enrollment api call failed");
        endDynatraceAction("SaleEnrollment", { taskId: enrollment.productOfferId, error: "EnrollmentError" });
        return { enrollmentStatus: EnrollmentStatus.Error };
    }
};
